import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import { FC, useEffect } from "react";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { createLocationNoteDtoSchema } from "service";
import { CreateLocationNoteDto } from "service/api/estabrookSchemas";

export interface EditNoteProps {
  onSave: (value?: string) => void;
  onClose: () => void;
  open?: boolean;
  inputValue?: string;
}

export const EditNote: FC<EditNoteProps> = (props) => {
  const { palette } = useTheme();
  const { onSave, onClose, inputValue, open, ...rest } = props;

  const {
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    resetForm,
    isValid,
  } = useFormik<CreateLocationNoteDto>({
    initialValues: {
      content: inputValue || "",
      mustRead: false,
    },
    validationSchema: toFormikValidationSchema(createLocationNoteDtoSchema),
    onSubmit: (values) => {
      if (values.content.trim()) {
        onSave(values.content);
      }
    },
  });

  // Reset form when dialog opens with new input value
  useEffect(() => {
    if (open && inputValue !== undefined) {
      resetForm({
        values: {
          content: inputValue,
          mustRead: false,
        },
      });
    }
  }, [open, inputValue, resetForm]);

  const handleSave = () => {
    if (!isValid || !values.content.trim()) {
      return;
    }
    onSave(values.content);
  };

  return (
    <Dialog onClose={onClose} {...rest} open={open || false}>
      <DialogTitle color={palette.grey[100]}>Edit Note</DialogTitle>
      <DialogContent>
        <TextField
          id="content"
          name="content"
          type="text"
          margin="dense"
          variant="outlined"
          rows={4}
          value={values.content}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.content && (errors.content as string)}
          error={touched.content && !!errors.content}
          fullWidth={true}
          multiline
          required
          style={{ width: "350px" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="close-button">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            !isValid || !values.content.trim() || values.content === inputValue
          }
          data-testid="confirm-button"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNote;
